import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Leaf, Star, ChevronRight, Camera, Sparkles } from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import { fadeUp, staggerContainer, staggerItem, pageTransition, viewportConfig } from '../components/AnimationVariants';

const CATEGORIES = ['All', 'Veneers', 'Whitening', 'Implants', 'Bonding', 'Invisalign'];

// For cases with `photo` only, a CSS filter simulates the dull "before" state on hover-off.
// For cases with `before`+`after`, real image pairs are shown.
const BEFORE_FILTER = 'brightness(0.7) saturate(0.25) contrast(1.1) sepia(0.2)';

const PX = (id, w = 600) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}&fit=crop`;

const CASES = [
  {
    id: 1,
    category: 'Veneers',
    title: 'Full Smile Transformation with Porcelain Veneers',
    tagline: '8 upper veneers, 3-week treatment',
    desc: 'This patient wanted to address years of discolouration and uneven edges. Eight ultra-thin porcelain veneers were custom-crafted to deliver a natural, radiant result they had always dreamed of.',
    // single photo — CSS filter shows dull "before", filter removed for bright "after"
    photo: PX(3762402),
    rating: 5,
    quote: 'I finally feel confident smiling in photos. The results are beyond what I imagined.',
  },
  {
    id: 2,
    category: 'Whitening',
    title: 'In-Office Teeth Whitening',
    tagline: '8 shades lighter in one visit',
    desc: 'A single 60-minute in-chair whitening session delivered a dramatic brightening result. The patient opted for our professional-grade whitening to prepare for a wedding — results were immediate and stunning.',
    // real pair: natural teeth before → bright white after
    before: PX(9324383),
    after: `https://images.pexels.com/photos/16212691/pexels-photo-16212691.png?auto=compress&cs=tinysrgb&w=600&fit=crop`,
    rating: 5,
    quote: 'Ready for my wedding day — my smile looks incredible. Worth every penny.',
  },
  {
    id: 3,
    category: 'Implants',
    title: 'Single Tooth Dental Implant',
    tagline: 'Natural-looking tooth replacement',
    desc: 'After losing a premolar to infection, this patient was self-conscious about the gap. A single titanium implant with a porcelain crown restored full function and aesthetics seamlessly.',
    photo: PX(28110692),
    rating: 5,
    quote: 'You genuinely cannot tell which tooth is the implant. Incredible craftsmanship.',
  },
  {
    id: 4,
    category: 'Bonding',
    title: 'Composite Bonding — Chips & Gaps',
    tagline: 'Same-day smile makeover',
    desc: 'Using tooth-coloured composite resin, we repaired two chipped front teeth and closed a central diastema in a single appointment — no anaesthetic, no prep, completely reversible.',
    photo: PX(3762453),
    rating: 5,
    quote: 'One appointment and my smile is completely different. Absolutely painless too.',
  },
  {
    id: 5,
    category: 'Invisalign',
    title: 'Invisalign Clear Aligner Treatment',
    tagline: '14 months to a perfectly aligned smile',
    desc: 'This adult patient had crowded lower teeth and a mild overbite. A custom Invisalign plan with 28 aligners corrected the alignment comfortably and discreetly over 14 months.',
    // real pair: braces (treatment need) → perfect aligned result
    before: PX(5842006),
    after: PX(3762468),
    rating: 5,
    quote: 'I wore the aligners to work every day — nobody noticed, but they all noticed my smile at the end.',
  },
  {
    id: 6,
    category: 'Veneers',
    title: 'Full Smile Makeover — Veneers & Whitening',
    tagline: 'Combined treatment, life-changing result',
    desc: 'A comprehensive smile makeover combining professional whitening on the lower arch and six porcelain veneers on the upper, creating a harmonious, balanced smile that looks completely natural.',
    photo: PX(3762402),
    rating: 5,
    quote: 'This has genuinely changed my life. I smile constantly now — something I never used to do.',
  },
];

const STATS = [
  { value: '500+', label: 'Smile Transformations' },
  { value: '98%', label: 'Patient Satisfaction' },
  { value: '15+', label: 'Years Experience' },
  { value: '4.9★', label: 'Average Rating' },
];

function BeforeAfterCard({ item }) {
  const [showAfter, setShowAfter] = useState(false);
  const hasRealPair = Boolean(item.before && item.after);

  return (
    <motion.div variants={staggerItem} className="group">
      <motion.div
        className="card-wood overflow-hidden h-full flex flex-col"
        whileHover={{ y: -4, boxShadow: '0 20px 48px rgba(45,90,61,0.15)' }}
        transition={{ type: 'spring', stiffness: 300, damping: 22 }}>

        {/* Image with before/after toggle */}
        <div className="relative overflow-hidden aspect-[4/3] cursor-pointer"
          onMouseEnter={() => setShowAfter(true)}
          onMouseLeave={() => setShowAfter(false)}
          onClick={() => setShowAfter((v) => !v)}>

          {hasRealPair ? (
            // Real before/after image pair — crossfade between two images
            <>
              <img
                src={item.before}
                alt={`Before — ${item.title}`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500"
                style={{ opacity: showAfter ? 0 : 1 }}
              />
              <img
                src={item.after}
                alt={`After — ${item.title}`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500"
                style={{ opacity: showAfter ? 1 : 0 }}
              />
            </>
          ) : (
            // Single photo — CSS filter dims to simulate "before"
            <img
              src={item.photo}
              alt={item.title}
              className="w-full h-full object-cover object-top transition-all duration-500"
              style={{ filter: showAfter ? 'none' : BEFORE_FILTER }}
            />
          )}

          {/* Label badge */}
          <div className="absolute top-3 left-3 z-10">
            <AnimatePresence mode="wait">
              <motion.span
                key={showAfter ? 'after' : 'before'}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.2 }}
                className={`text-xs font-bold px-3 py-1.5 rounded-full shadow-md ${
                  showAfter
                    ? 'bg-gold text-white'
                    : 'bg-white/90 text-gray-700'
                }`}>
                {showAfter ? 'AFTER' : 'BEFORE'}
              </motion.span>
            </AnimatePresence>
          </div>

          {/* Category badge */}
          <div className="absolute top-3 right-3 z-10">
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-forest/90 text-cream shadow-md">
              {item.category}
            </span>
          </div>

          {/* Hover hint */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-3 flex items-center justify-center gap-2"
            animate={{ opacity: showAfter ? 0 : 1 }}
            transition={{ duration: 0.3 }}>
            <Camera className="w-4 h-4 text-white/80" />
            <span className="text-white/90 text-xs font-medium">Hover to reveal result</span>
          </motion.div>
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-serif text-forest text-lg font-semibold mb-1">{item.title}</h3>
          <p className="text-gold text-xs font-semibold uppercase tracking-wider mb-3">{item.tagline}</p>
          <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{item.desc}</p>

          {/* Stars */}
          <div className="flex gap-0.5 mb-3">
            {Array.from({ length: item.rating }).map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-gold text-gold" />
            ))}
          </div>

          {/* Quote */}
          <blockquote className="border-l-2 border-sage pl-3 text-gray-500 text-xs italic leading-relaxed">
            "{item.quote}"
          </blockquote>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function SmileGallery() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? CASES
    : CASES.filter((c) => c.category === activeCategory);

  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -12, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <motion.div className="absolute left-0 bottom-0 w-48 opacity-10 hidden md:block rotate-180"
          animate={{ y: [0, 10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }}>
          <LeafCluster />
        </motion.div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> Real Patient Results
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            Smile Gallery
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed max-w-2xl mx-auto mb-8"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            Browse real before-and-after results from our patients. Each case reflects our commitment to natural aesthetics, clinical precision, and life-changing outcomes.
          </motion.p>
          <motion.div className="flex flex-wrap gap-3 justify-center"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.4 }}>
            <Link to="/contact" className="btn-primary text-sm py-2.5">
              <Sparkles className="w-4 h-4" /> Book a Consultation
            </Link>
            <Link to="/services" className="btn-outline-white text-sm py-2.5">
              View Treatments <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white border-b border-cream-deeper">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {STATS.map((stat) => (
              <motion.div key={stat.label} variants={staggerItem}>
                <div className="text-3xl font-serif font-bold text-forest mb-1">{stat.value}</div>
                <div className="text-gray-500 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="section-padding bg-cream">
        <div className="container-max">

          {/* Category filter */}
          <div className="text-center mb-10">
            <motion.p className="section-subtitle justify-center"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <Leaf className="w-4 h-4" /> Clinical Cases
            </motion.p>
            <motion.h2 className="section-title mb-8"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              Browse by Treatment
            </motion.h2>
            <motion.div className="flex flex-wrap gap-2 justify-center"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              {CATEGORIES.map((cat) => (
                <motion.button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    activeCategory === cat
                      ? 'bg-forest text-white shadow-md'
                      : 'bg-white text-gray-600 border border-cream-deeper hover:border-forest hover:text-forest'
                  }`}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}>
                  {cat}
                </motion.button>
              ))}
            </motion.div>
          </div>

          {/* Cards grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: 10 }}
              viewport={viewportConfig}>
              {filtered.map((item) => (
                <BeforeAfterCard key={item.id} item={item} />
              ))}
            </motion.div>
          </AnimatePresence>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">No cases found in this category yet.</div>
          )}
        </div>
      </section>

      {/* How we achieve results */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <p className="section-subtitle">
                <Leaf className="w-4 h-4" /> Our Approach
              </p>
              <h2 className="section-title mb-5">How We Achieve These Results</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Every smile makeover at Woodlands begins with a detailed consultation and digital smile design session. We use high-resolution photography, facial analysis, and mock-up techniques so you can preview your new smile before any treatment begins.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Digital Smile Design', desc: 'We map your facial proportions to design a smile that looks naturally yours.' },
                  { title: 'Minimal Preparation', desc: 'Our aesthetic techniques preserve as much natural tooth structure as possible.' },
                  { title: 'Custom Lab Work', desc: 'All restorations are crafted by specialist UK dental technicians to your exact specification.' },
                  { title: 'Staged Treatment Plans', desc: 'Complex makeovers are carefully sequenced to achieve the best outcome at every stage.' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-sage-pale flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-forest" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-forest text-sm mb-0.5">{item.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/5]"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}
              transition={{ delay: 0.15 }}>
              <img
                src="https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=700&auto=format&fit=crop&q=80"
                alt="Smile design consultation"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-2xl font-serif font-semibold mb-1">Your smile, designed</div>
                <p className="text-cream/80 text-sm">Preview your results before treatment begins.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials strip */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <div className="text-center mb-10">
            <motion.p className="section-subtitle justify-center"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <Star className="w-4 h-4 fill-current" /> Patient Stories
            </motion.p>
            <motion.h2 className="section-title"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              What Our Patients Say
            </motion.h2>
          </div>
          <motion.div className="grid md:grid-cols-3 gap-6"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {[
              {
                name: 'Sarah M.',
                treatment: 'Porcelain Veneers',
                text: 'I put this off for years thinking it would be painful or look fake. It was neither. My veneers look so natural that even my family couldn\'t tell at first.',
              },
              {
                name: 'James T.',
                treatment: 'Dental Implants',
                text: 'After losing a tooth in an accident, I was devastated. The implant looks and feels completely like my own tooth. The care I received was exceptional.',
              },
              {
                name: 'Claire B.',
                treatment: 'Invisalign',
                text: 'I\'m in my 40s and always thought braces were just for teenagers. Invisalign changed that — totally discreet and the results are amazing.',
              },
            ].map((t) => (
              <motion.div key={t.name} variants={staggerItem} className="card-wood p-6">
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div>
                  <div className="font-semibold text-forest text-sm">{t.name}</div>
                  <div className="text-gray-400 text-xs">{t.treatment}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-16 bg-cream">
        <div className="container-max">
          <motion.div className="bg-forest rounded-3xl p-8 md:p-12 text-white text-center"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <h2 className="font-serif text-3xl font-semibold mb-3">Ready to transform your smile?</h2>
            <p className="text-cream/75 mb-7 max-w-lg mx-auto">
              Book a free smile consultation and let us show you what's possible. No pressure, no commitment — just a conversation about your smile goals.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="btn-primary">
                <Sparkles className="w-4 h-4" /> Book Free Consultation
              </Link>
              <Link to="/fees-plans" className="btn-outline-white">
                View Pricing <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
